import {Box, Button, Grid, MenuItem, MenuList, makeStyles} from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CheckIcon from '@material-ui/icons/Check';
import RemoveIcon from '@material-ui/icons/Remove';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import CRM from '../../APIs/CRM';
import {CategoryHeader, Spacing} from '../../Core/UI';
import {useAfterSaleTemplateData} from '../../Core/hooks/useTemplateData';
import {Approval} from '../../Core/mobx/ParameterStore/Approval';
import {approvalGuards} from '../../Core/mobx/ParameterStore/ApprovalGuards';
import {fieldCategory} from '../../Core/mobx/ParameterStore/ApprovalXref';
import {Parameter, ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import {Categories} from '../../Core/mobx/ParameterStore/types';
import RootStore from '../../Core/mobx/RootStore';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import {TemplatePurposes, TemplateTypes} from '../ContactForm/EmailEditor/EmailEditor';
import EmailEditorDialog from '../ContactForm/EmailEditor/EmailEditorDialog';
import {onEditorSend} from '../ContactForm/EmailEditor/EmailEditorSelection';
import {FormType} from '../PreQualification/types';
import RxDialog from '../Tools/RxDialog';
import {ToolsPanel} from '../Tools/Tools';
import {PolicyInfoCarrierNames, PolicyInfoProductTypes, TemplateAfterSaleQuoteData} from '../types';
import AgentInfoSection from './Components/AgentInfo';
import Bankruptcy from './Components/Bankruptcy';
import BasicInfoSection from './Components/BasicInfo';
import Beneficiaries from './Components/Beneficiaries';
import ConditionalCoverageBanking from './Components/CoverageBanking';
import CriminalOffense from './Components/CriminalOffense';
import DUI from './Components/DUI';
import Dependence from './Components/Dependence';
import EmergencyContact from './Components/EmergencyContact';
import ExistingPolicies from './Components/ExistingPolicies';
import FGHealth from './Components/FGHealth';
import FGPersonal from './Components/FGPersonal';
import FamilyHistory from './Components/FamilyHistory';
import Financials from './Components/Financials';
import HealthInfo from './Components/Health';
import Identity from './Components/Identity';
import License from './Components/License';
import MovingViolations from './Components/MovingViolations';
import Permissions from './Components/Permissions';
import Physician from './Components/Physician';
import PolicyInformation from './Components/PolicyInformation';
import TobaccoInfo from './Components/TobaccoInfo';
import UpsellOpportunities from './Components/UpsellOpportunities';
import {IS_PRODUCTION} from '@/constants';
import {AddressKey} from '../../Core/mobx/AddressVerificationStore/AddressVerificationStore';

enum DisabledMessages {
    submited = 'The form has already been submited',
    requiredFiledMissing = 'field missing',
    bankingValidationFailed = '[Banking Information] Banking Validation Failed',
    clientAddressVerificationRequired = '[Basic Information] Client Address Verification Required',
    policyOwnerAddressVerificationRequired = '[Basic Information] Policy Owner Address Verification Required',
}

const isErrorMessage = (msg: string) =>
    msg === DisabledMessages.bankingValidationFailed ||
    msg === DisabledMessages.clientAddressVerificationRequired ||
    msg === DisabledMessages.policyOwnerAddressVerificationRequired;

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
    },
    categoryListItemContainer: {
        display: 'flex',
    },
    categoryListItemIcon: {
        marginRight: '5px',
        width: '16px',
    },
    disabledMessageContainer: {
        marginBottom: 20,
        marginTop: 20,
        marginLeft: 15,
        top: '50px',
        left: '13px',
        fontSize: '11px',
        color: 'gray',
    },
    formSubmissionErrors: {
        marginTop: 0,
        fontSize: '11px',
        color: 'red',
    },
}));

type ValueChanged = (p?: Parameter, value?: string | number | boolean | undefined, formType?: FormType) => void;

export default observer(
    ({
        parameterStore,
        valueChanged,
    }: {
        parameterStore: ParameterStore;
        valueChanged: ValueChanged;
        formDirty: boolean | undefined;
    }) => {
        const {historyFormId} = useParams<any>();
        const [showVerifyDialog, setShowVerifyDialog] = useState(false);

        const [verifyLink, setVerifyLink] = useState('');
        const [verifyEmail, setVerifyEmail] = useState('');
        const [verifyPhone, setVerifyPhone] = useState('');
        const [openAccordions, setOpenAccordions] = useState<string[]>(
            RootStore.agentPortalStore.parameterStore.approval.agentProducts.value?.length
                ? RootStore.agentPortalStore.parameterStore.approval.agentProducts.value
                : ['LSWIUL'],
        );
        const warningOffset =
            (!IS_PRODUCTION ? 76 : 0) +
            (parameterStore && !RootStore.currentAgentStates.includes(parameterStore.approval.state.value) ? 80 : 0);
        const [assistPanelExpanded, setAssistPanelExpanded] = useState(true);
        let params: [string, Parameter][] = Object.entries(parameterStore?.approval)
            .filter(([n, _]) => n !== 'calculatedContNumberOfBenes') // filter out calculated field
            .filter(
                ([n, _]) =>
                    !approvalGuards.hasOwnProperty(n) ||
                    approvalGuards[n as keyof typeof approvalGuards](parameterStore),
            );

        const hashFieldsByCategory: {
            [category: string]: (Parameter & {name: string})[];
        } = {};
        params?.forEach(([name, p]) => {
            if (!fieldCategory.hasOwnProperty(name)) return;
            const category = fieldCategory[name as keyof typeof fieldCategory];

            if (!hashFieldsByCategory[category]) hashFieldsByCategory[category] = [];

            hashFieldsByCategory[category].push({...p, name: name});
        });
        const classes = useStyles();
        const {approval, health, prequal} = parameterStore || {};
        const [activeCategory, setActiveCategory] = useState('');
        const [loading, setLoading] = useState(false);
        const scrollTimeout = useRef<ReturnType<typeof setTimeout>>();
        const [valid, setValid] = useState(false);
        const [submitted, setSubmitted] = useState(false);
        const [disabledMessage, setDisabledMessage] = useState('');
        const {contactsFormStore} = RootStore.agentPortalStore;
        const email = contactsFormStore.contact ?? parameterStore?.contact;
        const firstName = contactsFormStore.name?.split(' ')?.[0];
        const [openEmailEditor, setOpenEmailEditor] = useState(false);
        const [emailTemplateType, setEmailTemplateType] = useState<TemplateTypes>(TemplateTypes.general);
        const [emailTemplatePurpose, setEmailTemplatePurpose] = useState<TemplatePurposes>(TemplatePurposes.afterSale);

        // Fetch recommended products if not already loaded
        useEffect(() => {
            const fetchProductsIfNeeded = async () => {
                // Check if products are not loaded
                if (
                    !parameterStore.recommendedProductsResponse ||
                    !parameterStore.recommendedProductsResponse.allProducts
                ) {
                    try {
                        await parameterStore.fetchRecommendedProducts();
                    } catch (error) {
                        console.error('Failed to fetch recommended products:', error);
                    }
                }
            };

            fetchProductsIfNeeded();
        }, [parameterStore]);

        useEffect(() => {
            if (parameterStore.approval.agentProducts.value) {
                setOpenAccordions(
                    parameterStore.approval.agentProducts.value?.length
                        ? parameterStore.approval.agentProducts.value
                        : ['LSWIUL'],
                );
            }
        }, [RootStore.agentPortalStore.parameterStore.approval.agentProducts.value]);
        const getFirstCarrier = (): TemplateAfterSaleQuoteData => {
            const reapplyCarrier: TemplateAfterSaleQuoteData = {
                carrierQuoted: approval.reapplyCarrierQuoted?.value,
                productType: approval.reapplyProductType?.value,
                coverageAmount: approval.reapplyCoverageAmount?.value,
                monthlyPremium: approval.reapplyMonthlyPremium?.value,
            };
            if (
                !!reapplyCarrier &&
                !!reapplyCarrier.carrierQuoted &&
                !!reapplyCarrier.productType &&
                !!reapplyCarrier.coverageAmount &&
                !!reapplyCarrier.monthlyPremium
            )
                return reapplyCarrier;
            return {
                carrierQuoted: approval.firstCarrierQuoted?.value,
                productType: approval.firstProductType?.value,
                coverageAmount: approval.firstCoverageAmount?.value,
                monthlyPremium: approval.firstMonthlyPremium?.value,
            };
        };
        const getSecondCarrier = (): TemplateAfterSaleQuoteData | undefined => {
            const secondCarrier = {
                carrierQuoted: approval.secondCarrierQuoted?.value,
                productType: approval.secondProductType?.value,
                coverageAmount: approval.secondCoverageAmount?.value,
                monthlyPremium: approval.secondMonthlyPremium?.value,
            };
            if (
                !!secondCarrier &&
                !!secondCarrier.carrierQuoted &&
                !!secondCarrier.productType &&
                !!secondCarrier.coverageAmount &&
                !!secondCarrier.monthlyPremium
            )
                return secondCarrier;
            return undefined;
        };

        // consider combination if we have first + second quote
        const isCombination = !!getFirstCarrier().productType && !!getSecondCarrier()?.productType;
        const templateData = useAfterSaleTemplateData(firstName, getFirstCarrier(), getSecondCarrier());

        if (parameterStore && parameterStore.submitted && !submitted) {
            setSubmitted(true);
            setDisabledMessage(DisabledMessages.submited);
        }

        const validate = () => {
            if (!params) return false;

            let isValid = true;
            let firstRequiredFieldMissing = '';
            let minLengthCheckMessage = '';
            params.every((p) => {
                const [name, param] = p;
                const required = Approval.isRequiredField(
                    name,
                    RootStore.agentPortalStore.parameterStore.approval.agentProducts.value,
                    RootStore.agentPortalStore.parameterStore.approval.existingCoverage.value,
                    RootStore.agentPortalStore.parameterStore.approval.hasPrimaryCarePhysician.value,
                    RootStore.agentPortalStore.parameterStore.approval.fgMarijuanaPast5Years.value,
                    false,
                    !RootStore.agentPortalStore.parameterStore.approval.conditionalNextAvailable.value,
                );
                const category = fieldCategory.hasOwnProperty(name)
                    ? fieldCategory[name as keyof typeof fieldCategory]
                    : undefined;
                const isParamValid =
                    (required && param.value !== null && param.value !== undefined && param.value !== '') || !required;
                if (!isParamValid) {
                    isValid = false;
                    if (!firstRequiredFieldMissing) {
                        firstRequiredFieldMissing = `[${category}] ${name}`;
                    }
                    return false;
                }
                return true;
            });

            const messages: string[] = [];

            // Check GIACT validation status
            const hasPrimaryBankInfo =
                approval.conditionalRoutingNumber?.value || approval.conditionalAccountNumber?.value;
            const hasSecondaryBankInfo =
                approval.secondaryRoutingNumber?.value || approval.secondaryAccountNumber?.value;
            const hasBankInfo = hasPrimaryBankInfo || hasSecondaryBankInfo;

            if (hasBankInfo) {
                const primaryComplete =
                    approval.conditionalRoutingNumber?.value && approval.conditionalAccountNumber?.value;
                const secondaryComplete =
                    approval.secondaryRoutingNumber?.value && approval.secondaryAccountNumber?.value;

                const primaryValidated =
                    primaryComplete &&
                    approval.primaryAccountValidationDate?.value &&
                    approval.primaryAccountValidationAgent?.value &&
                    !approval.primaryAccountValidationError?.value;
                const secondaryValidated =
                    secondaryComplete &&
                    approval.secondaryAccountValidationDate?.value &&
                    approval.secondaryAccountValidationAgent?.value &&
                    !approval.secondaryAccountValidationError?.value;

                if (!primaryValidated && !secondaryValidated) {
                    isValid = false;
                    messages.push(DisabledMessages.bankingValidationFailed);
                }
            }

            // Check address verification
            const addressStore = RootStore.addressVerificationStore;
            if (!addressStore.canSubmit('client')) {
                isValid = false;
                messages.push(DisabledMessages.clientAddressVerificationRequired);
            }
            if (approval.sameOwner.value === false && !addressStore.canSubmit('policyOwner')) {
                isValid = false;
                messages.push(DisabledMessages.policyOwnerAddressVerificationRequired);
            }

            if (firstRequiredFieldMissing) {
                messages.unshift(`${firstRequiredFieldMissing} ${DisabledMessages.requiredFiledMissing}`);
            }
            if (minLengthCheckMessage) {
                messages.unshift(minLengthCheckMessage);
            }

            setDisabledMessage(messages.join('\n'));

            return isValid;
        };

        const submit = async () => {
            setLoading(true);

            await RootStore.agentPortalStore.contactsFormStore.submitForm('preapproval');
            setSubmitted(true);
            setLoading(false);
            setDisabledMessage(DisabledMessages.submited);
        };

        const selectItem = (categoryId: string, index: number) => {
            const element = document.getElementById(`${categoryId}_${index}_header`);
            const container = document.getElementById('tab2');
            setActiveCategory(categoryId);

            element?.scrollIntoView({block: 'start', behavior: 'smooth'});

            const callback = () => {
                clearTimeout(scrollTimeout.current as unknown as number);
                scrollTimeout.current = setTimeout(() => {
                    container?.removeEventListener('scroll', callback);
                }, 100);
            };

            container?.addEventListener('scroll', callback);
        };

        const getVerifyLink = async () => {
            let respCode;
            await RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');
            const formId = parameterStore?.formid;
            const email = parameterStore.approval.emailAddress.value;
            const phNo = parameterStore.approval.phoneNumber.value;
            setVerifyEmail(email);
            setVerifyPhone(phNo);

            if (formId && email) {
                respCode = await CRM.GetCustomerVerifyDetailCode(formId, email);
            }
            return respCode;
        };
        const onCloseVerifyDialog = async () => {
            setShowVerifyDialog(false);
        };

        const onEditorClose = () => {
            setOpenEmailEditor(false);
        };

        const showEmailEditor = (purpose: TemplatePurposes) => {
            if (!email) {
                RootStore.uiStore.showSnackbar('Email address is empty', 'error');
                return;
            }

            const {productType: ptype, carrierQuoted} = getFirstCarrier();
            let templateType: TemplateTypes = TemplateTypes.general;
            switch (ptype) {
                case PolicyInfoProductTypes.TERM_10:
                case PolicyInfoProductTypes.TERM_15:
                case PolicyInfoProductTypes.TERM_20:
                case PolicyInfoProductTypes.TERM_30:
                    templateType = TemplateTypes.quoteTermNonblend;
                    break;
                case PolicyInfoProductTypes.IUL:
                    templateType =
                        carrierQuoted === PolicyInfoCarrierNames.EMC
                            ? TemplateTypes.quoteProsperIULNonblend
                            : TemplateTypes.quoteIulNonblend;
                    break;
                case PolicyInfoProductTypes.GUL:
                    templateType = TemplateTypes.quoteGulNonblend;
                    break;
                case PolicyInfoProductTypes.GI:
                    templateType = TemplateTypes.quoteGuaranteedNonblend;
                    break;
                case PolicyInfoProductTypes.VUL:
                    templateType = TemplateTypes.quoteVULNonblend;
                    break;
                default:
                    break;
            }

            if (isCombination) {
                const singleQuoteType = getFirstCarrier().productType;
                const combinedQuoteType = getSecondCarrier()?.productType;

                const getCombinedTemplateType = (
                    template: TemplateTypes,
                    first: PolicyInfoProductTypes,
                    second?: PolicyInfoProductTypes,
                ) => {
                    if (first && second) {
                        const hasTerm = [first, second].some((v) =>
                            [
                                PolicyInfoProductTypes.TERM_10,
                                PolicyInfoProductTypes.TERM_15,
                                PolicyInfoProductTypes.TERM_20,
                                PolicyInfoProductTypes.TERM_30,
                            ].includes(v),
                        );
                        if (hasTerm) {
                            if ([first, second].some((v) => v === PolicyInfoProductTypes.GUL))
                                return TemplateTypes.quoteGULTermBlend;
                            if ([first, second].some((v) => v === PolicyInfoProductTypes.IUL))
                                return TemplateTypes.quoteIULTermBlend;
                            if ([first, second].some((v) => v === PolicyInfoProductTypes.VUL))
                                return TemplateTypes.quoteVULTermBlend;
                        }
                    }
                    return template;
                };
                templateType = getCombinedTemplateType(templateType, singleQuoteType, combinedQuoteType);
            }

            setEmailTemplatePurpose(purpose);
            setEmailTemplateType(templateType);
            setOpenEmailEditor(true);
        };

        useEffect(() => {
            if (!activeCategory && Object.keys(Categories)?.length) {
                setActiveCategory(Categories.AGENT_INFORMATION.replaceAll(/ |&/g, '_'));
            }
        }, [Categories]);

        useEffect(() => {
            const isValid = validate();
            setValid(isValid);
        }, [
            params,
            // Explicitly watch bank-related fields to ensure validation reruns
            approval.conditionalRoutingNumber?.value,
            approval.conditionalAccountNumber?.value,
            approval.secondaryRoutingNumber?.value,
            approval.secondaryAccountNumber?.value,
            approval.primaryAccountValidationDate?.value,
            approval.primaryAccountValidationAgent?.value,
            approval.primaryAccountValidationError?.value,
            approval.secondaryAccountValidationDate?.value,
            approval.secondaryAccountValidationAgent?.value,
            approval.secondaryAccountValidationError?.value,
            // Address verification state
            RootStore.addressVerificationStore.canSubmit('client'),
            RootStore.addressVerificationStore.canSubmit('policyOwner'),
            approval.sameOwner?.value,
        ]);

        const onAcceptStandardized = (key: AddressKey) => {
            const store = RootStore.addressVerificationStore;
            const state = store.getState(key);
            if (!state.result?.standardizedAddress) return;
            const std = state.result.standardizedAddress;
            if (key === 'client') {
                valueChanged(approval.address, std.street);
                valueChanged(approval.addressCity, std.city);
                valueChanged(approval.state, std.state);
                valueChanged(approval.addressZipCode, std.zip);
                if (std.unit !== undefined) valueChanged(approval.addressUnit, std.unit);
            } else {
                valueChanged(approval.policyOwnerStreetAddress, std.street);
                valueChanged(approval.policyOwnerCity, std.city);
                valueChanged(approval.policyOwnerState, std.state);
                valueChanged(approval.policyOwnerZipcode, std.zip);
                if (std.unit !== undefined) valueChanged(approval.policyOwnerUnit, std.unit);
            }
            store.acceptStandardized(key);
        };

        if (parameterStore === null) return null;

        const sectionConfig = [
            {key: Categories.AGENT_INFORMATION, Component: AgentInfoSection},
            {key: Categories.BASIC_INFORMATION, Component: BasicInfoSection, props: {onAcceptStandardized}},
            {key: Categories.IDENTITY, Component: Identity},
            {key: Categories.BENEFICIARIES, Component: Beneficiaries},
            {key: Categories.EXISTING_POLICIES, Component: ExistingPolicies},
            {key: '', productKey: 'FGPathsetterIUL', title: 'F&G Pathsetter IUL'},
            {key: '', productKey: 'FGEverlast', title: 'F&G Everlast'},
            ...(openAccordions.includes('FGPathsetterIUL') || openAccordions.includes('FGEverlast')
                ? [
                      {key: Categories.FG_HEALTH, Component: FGHealth},
                      {key: Categories.FG_PERSONAL, Component: FGPersonal},
                  ]
                : []),
            {key: '', productKey: 'LSWIUL', title: 'LSW IUL'},
            {key: '', productKey: 'Prosper', title: 'Prosper'},
            ...(openAccordions.includes('LSWIUL') || openAccordions.includes('Prosper')
                ? [
                      {
                          key: Categories.HEALTH,
                          Component: HealthInfo,
                          props: {healthParameters: health},
                      },
                      approval.DrugorAlcoholDependenceWithdrawal.value && {
                          key: Categories.DRUG_OR_ALCOHOL_DEPENDENCE_WITHDRAWAL,
                          Component: Dependence,
                      },
                      approval.DUI.value && {key: Categories.DUI, Component: DUI},
                      approval.License.value && {key: Categories.LICENSE, Component: License},
                      approval.Movingviolations.value && {
                          key: Categories.MOVING_VIOLATIONS,
                          Component: MovingViolations,
                      },
                      approval.Criminaloffense.value && {
                          key: Categories.CRIMINAL_OFFENSE,
                          Component: CriminalOffense,
                      },
                      approval.Bankruptcy.value && {
                          key: Categories.BANKRUPTCY,
                          Component: Bankruptcy,
                      },
                      {key: Categories.TOBACCO_AND_MARIJUANA, Component: TobaccoInfo},
                  ]
                : []),
            {key: '', productKey: 'EquitableVUL', title: 'Equitable VUL'},
            ...(openAccordions.includes('EquitableVUL')
                ? [
                      {
                          key: Categories.EQUITABLEVUL_HEALTH,
                          Component: HealthInfo,
                          props: {healthParameters: health},
                      },
                      approval.DrugorAlcoholDependenceWithdrawal.value && {
                          key: Categories.DRUG_OR_ALCOHOL_DEPENDENCE_WITHDRAWAL,
                          Component: Dependence,
                      },
                      approval.DUI.value && {key: Categories.DUI, Component: DUI},
                      approval.License.value && {key: Categories.LICENSE, Component: License},
                      approval.Movingviolations.value && {
                          key: Categories.MOVING_VIOLATIONS,
                          Component: MovingViolations,
                      },
                      approval.Criminaloffense.value && {
                          key: Categories.CRIMINAL_OFFENSE,
                          Component: CriminalOffense,
                      },
                      approval.Bankruptcy.value && {
                          key: Categories.BANKRUPTCY,
                          Component: Bankruptcy,
                      },
                      {key: Categories.TOBACCO_AND_MARIJUANA, Component: TobaccoInfo},
                  ]
                : []),
            {key: Categories.FAMILY_HISTORY, Component: FamilyHistory},
            {key: Categories.PHYSICIAN, Component: Physician},
            ...(openAccordions.includes('EquitableVUL')
                ? [
                      {key: Categories.EMERGENCY_CONTACT_VUL_ONLY, Component: EmergencyContact},
                      {key: Categories.FINANCIALS_VUL_ONLY, Component: Financials},
                  ]
                : []),
            {key: Categories.PERMISSIONS, Component: Permissions},
            {key: Categories.POLICY_INFORMATION, Component: PolicyInformation},
            {
                key: Categories.BANKING_INFORMATION,
                Component: ConditionalCoverageBanking,
            },
            {key: Categories.UPSELL_OPPORTUNITIES, Component: UpsellOpportunities},
        ].filter(Boolean);

        const renderSection = (item: any, index: any) => {
            const {key, Component, props, productKey, title} = item;
            if (key === '' && productKey && title)
                return openAccordions.includes(productKey) ? (
                    <Box
                        style={{
                            paddingLeft: 30,
                            paddingTop: 40,
                            paddingBottom: 20,
                            borderTop: '1px solid #e1e1e1',
                        }}
                    >
                        <CategoryHeader name={title} error={false} />
                    </Box>
                ) : (
                    <></>
                );
            return (
                <Component
                    {...props}
                    valueChanged={valueChanged}
                    parameters={approval}
                    setCategory={setActiveCategory}
                    index={index + 1 - (sectionConfig.filter((t, i) => i < index && t.key === '')?.length ?? 0)}
                />
            );
        };
        const gridContainerStyle = useMemo(
            () => ({height: `calc(100vh - ${116 + warningOffset}px)`, overflowY: 'hidden' as const}),
            [warningOffset],
        );

        return (
            <Grid container spacing={3} style={gridContainerStyle}>
                <Grid
                    item
                    xs={assistPanelExpanded ? 8 : 11}
                    style={{paddingTop: 40, height: '100%', overflowY: 'auto'}}
                >
                    <Card>{sectionConfig.map(renderSection)}</Card>
                    <Spacing height={20} />
                    <ToolsPanel parameterStore={parameterStore} fullWidth />
                </Grid>
                <Grid
                    item
                    xs={assistPanelExpanded ? 4 : 1}
                    style={{
                        paddingTop: 20,
                        height: '100%',
                        overflowY: 'auto',
                        ...(!assistPanelExpanded && {display: 'flex', justifyContent: 'center'}),
                    }}
                >
                    <AgentAssistPanel
                        tab="preapproval"
                        doOnExpand={() => setAssistPanelExpanded(true)}
                        doOnCollapse={() => setAssistPanelExpanded(false)}
                        nestPreappChecklist={
                            <>
                                <MenuList>
                                    {sectionConfig.map(({key, productKey, title}, index) => {
                                        if (key === '')
                                            return (productKey &&
                                                RootStore.agentPortalStore.parameterStore.approval.agentProducts.value?.includes(
                                                    productKey,
                                                )) ||
                                                (!RootStore.agentPortalStore.parameterStore.approval.agentProducts.value
                                                    ?.length &&
                                                    productKey === 'LSWIUL') ? (
                                                <MenuItem key={index}>{title}</MenuItem>
                                            ) : null;
                                        const categoryId = key.replaceAll(/ |&/g, '_');
                                        const text = <div>{key}</div>;
                                        let icon = (
                                            <RemoveIcon
                                                className={classes.categoryListItemIcon}
                                                style={{color: 'gray'}}
                                            />
                                        );

                                        const checked =
                                            hashFieldsByCategory[key]?.filter(
                                                (param) =>
                                                    Approval.isRequiredField(
                                                        param.name,
                                                        RootStore.agentPortalStore.parameterStore.approval.agentProducts
                                                            .value,
                                                        RootStore.agentPortalStore.parameterStore.approval
                                                            .existingCoverage.value,
                                                        RootStore.agentPortalStore.parameterStore.approval
                                                            .hasPrimaryCarePhysician.value,
                                                        RootStore.agentPortalStore.parameterStore.approval
                                                            .fgMarijuanaPast5Years.value,
                                                        false,
                                                        !RootStore.agentPortalStore.parameterStore.approval
                                                            .conditionalNextAvailable.value,
                                                    ) &&
                                                    (param.value === '' || param.value === null),
                                            ).length === 0;
                                        if (checked) {
                                            icon = (
                                                <CheckIcon
                                                    className={classes.categoryListItemIcon}
                                                    style={{color: 'green'}}
                                                />
                                            );
                                        }

                                        const toDisplay = (
                                            <div className={classes.categoryListItemContainer}>
                                                {icon}
                                                {text}
                                            </div>
                                        );

                                        return (
                                            <MenuItem
                                                onClick={() =>
                                                    selectItem(
                                                        categoryId,
                                                        index -
                                                            sectionConfig.slice(0, index).filter((t) => !t.key).length +
                                                            1,
                                                    )
                                                }
                                                selected={categoryId === activeCategory}
                                                key={index}
                                            >
                                                {toDisplay}
                                            </MenuItem>
                                        );
                                    })}
                                </MenuList>
                                <div style={{position: 'relative'}}>
                                    <Button
                                        disabled={loading || !valid || historyFormId}
                                        variant="contained"
                                        color="primary"
                                        style={{
                                            marginTop: 12,
                                            marginRight: 10,
                                            marginLeft: 10,
                                        }}
                                        onClick={submit}
                                    >
                                        Submit Pre-App
                                    </Button>
                                    <Button
                                        disabled={loading || !valid || historyFormId}
                                        variant="outlined"
                                        color="primary"
                                        style={{
                                            marginTop: 12,
                                            marginRight: 10,
                                            marginLeft: 10,
                                        }}
                                        onClick={() => showEmailEditor(TemplatePurposes.afterSale)}
                                    >
                                        After Sale
                                    </Button>
                                    {(!valid || submitted) && (
                                        <div className={classes.disabledMessageContainer}>
                                            {disabledMessage.split('\n').map((msg, i) =>
                                                isErrorMessage(msg) ? (
                                                    <div key={i} className={classes.formSubmissionErrors}>
                                                        {msg}
                                                    </div>
                                                ) : (
                                                    <div key={i}>{msg}</div>
                                                ),
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        }
                        onValueChangedNotes={(value) => valueChanged({...approval.notes, name: 'notes'}, value)}
                    />
                </Grid>
                <RxDialog
                    open={showVerifyDialog}
                    onClose={onCloseVerifyDialog}
                    link={verifyLink}
                    email={verifyEmail}
                    setEmail={setVerifyEmail}
                    phone={verifyPhone}
                    setPhone={setVerifyPhone}
                    type="verify"
                />
                <EmailEditorDialog
                    open={openEmailEditor}
                    onClose={onEditorClose}
                    onSend={(
                        emails: string[],
                        html: string,
                        productType: string,
                        templatePurpose: TemplatePurposes,
                        bccEmails?: string[],
                        subject?: string,
                    ) => {
                        onEditorSend(emails, html, productType, templatePurpose, bccEmails, subject).then(() => {
                            onEditorClose();
                        });
                    }}
                    currentEmails={[email]}
                    templatePurpose={emailTemplatePurpose}
                    templateType={emailTemplateType}
                    templateData={[templateData]}
                />
            </Grid>
        );
    },
);
